import { useState, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';

export interface UseExpirationDateResult {
  // State
  hasExpiration: boolean;
  expirationDate: Dayjs | null;
  expirationError: string | null;

  // Actions
  setHasExpiration: (value: boolean) => void;
  setExpirationDate: (date: Dayjs | null) => void;
  setExpirationError: (error: string | null) => void;

  // Utilities
  getFormattedDate: () => string | null;
  validateExpirationDate: () => boolean;
  resetExpiration: () => void;
  initializeFromDate: (dateString?: string | null) => void;
}

export const useExpirationDate = (): UseExpirationDateResult => {
  const [hasExpiration, setHasExpirationState] = useState(false);
  const [expirationDate, setExpirationDateState] = useState<Dayjs | null>(null);
  const [expirationError, setExpirationError] = useState<string | null>(null);

  const setHasExpiration = useCallback((value: boolean) => {
    setHasExpirationState(value);
    if (!value) {
      setExpirationDateState(null);
      setExpirationError(null);
    }
  }, []);

  const setExpirationDate = useCallback(
    (date: Dayjs | null) => {
      setExpirationDateState(date);
      if (expirationError) {
        setExpirationError(null);
      }
    },
    [expirationError],
  );

  const getValidExpirationDate = useCallback((): Dayjs | null => {
    if (!expirationDate) {
      return null;
    }

    const normalizedDate = dayjs(expirationDate);
    return normalizedDate.isValid() ? normalizedDate : null;
  }, [expirationDate]);

  const getFormattedDate = useCallback((): string | null => {
    if (!hasExpiration) {
      return null;
    }

    const validDate = getValidExpirationDate();
    return validDate ? validDate.toISOString() : null;
  }, [hasExpiration, getValidExpirationDate]);

  const validateExpirationDate = useCallback((): boolean => {
    if (!hasExpiration) {
      setExpirationError(null);
      return true;
    }

    if (!expirationDate) {
      setExpirationError('Please select an expiration date');
      return false;
    }

    const validDate = getValidExpirationDate();
    if (!validDate) {
      setExpirationError('Expiration date must be valid date and time');
      return false;
    }

    // Check if date is in the future
    if (validDate.isBefore(dayjs())) {
      setExpirationError('Expiration date must be in the future');
      return false;
    }

    setExpirationError(null);
    return true;
  }, [hasExpiration, expirationDate, getValidExpirationDate]);

  const resetExpiration = useCallback(() => {
    setHasExpirationState(false);
    setExpirationDateState(null);
    setExpirationError(null);
  }, []);

  const initializeFromDate = useCallback((dateString?: string | null) => {
    if (dateString) {
      setHasExpirationState(true);
      setExpirationDateState(dayjs(dateString));
    } else {
      setHasExpirationState(false);
      setExpirationDateState(null);
    }
    setExpirationError(null);
  }, []);

  return {
    // State
    hasExpiration,
    expirationDate,
    expirationError,

    // Actions
    setHasExpiration,
    setExpirationDate,
    setExpirationError,

    // Utilities
    getFormattedDate,
    validateExpirationDate,
    resetExpiration,
    initializeFromDate,
  };
};
